/**
 * Rattachement par lien d'invitation.
 *
 * Le gestionnaire crée une invitation et transmet un lien de la forme
 * `https://app.autolink.lu/?invite=<token>`. Jusqu'ici, ce lien ne servait à
 * rien : le jeton était bien produit et `invitation.accept` existait côté
 * API, mais aucun écran ne lisait le paramètre. Le conducteur cliquait,
 * s'inscrivait, et restait sans société — sans qu'aucun message ne le dise.
 *
 * Ce hook ferme la boucle :
 *
 *  1. au tout premier rendu, le jeton est retiré de l'URL et mis de côté dans
 *     `sessionStorage` — il doit survivre à l'inscription et à la connexion,
 *     qui ne préservent pas les paramètres d'URL ;
 *  2. dès que l'utilisateur est connecté et n'a pas encore de société, le
 *     rattachement est tenté une seule fois ;
 *  3. le jeton est effacé dans tous les cas — réussite, expiration ou refus.
 *     Un jeton qui traîne serait rejoué à chaque démarrage.
 *
 * Le jeton est retiré de l'URL immédiatement : il ne doit pas rester dans la
 * barre d'adresse, ni dans l'historique, ni partir en `Referer` vers les CDN
 * de polices et d'icônes.
 */
import { useEffect, useRef, useState } from 'react';
import { fleetService } from '@/services';
import { authService } from '@/services/auth.service';
import { ApiError } from '@/lib/http';

const KEY = 'autolink.invite';

/** Capture au chargement du module, avant tout rendu. */
function captureFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('invite');
    if (!token) return;
    sessionStorage.setItem(KEY, token);
    url.searchParams.delete('invite');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* URL exotique : on n'insiste pas */
  }
}
captureFromUrl();

export type InviteState =
  | { status: 'idle' }
  | { status: 'joining' }
  | { status: 'joined' }
  | { status: 'failed'; message: string };

/** Un jeton attend d'être consommé : l'écran d'accueil doit le dire. */
export function hasPendingInvite(): boolean {
  try {
    return !!sessionStorage.getItem(KEY);
  } catch {
    return false;
  }
}

/** Abandon explicite : « je ne suis pas la personne invitée ». */
export function dropPendingInvite(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* rien à faire */
  }
}

export function useInvite(isSignedIn: boolean, hasCompany: boolean): InviteState {
  const [state, setState] = useState<InviteState>({ status: 'idle' });
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !isSignedIn || hasCompany) return;

    const token = sessionStorage.getItem(KEY);
    if (!token) return;

    done.current = true;
    setState({ status: 'joining' });

    void fleetService
      .acceptInvite(token)
      .then(async () => {
        await authService.refreshProfile();
        setState({ status: 'joined' });
      })
      .catch((e) => {
        setState({
          status: 'failed',
          message:
            e instanceof ApiError
              ? e.message
              : "Ce lien d'invitation n'est plus valable.",
        });
      })
      .finally(() => sessionStorage.removeItem(KEY));
  }, [isSignedIn, hasCompany]);

  return state;
}
